import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { canAccess } from '../context/UserContext';
import { REGISTERED_PAGES } from '../navRegistry';
import SearchableSelect from '../components/SearchableSelect';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { useNavRefresh } from '../context/NavContext';

const API = process.env.REACT_APP_API_URL;
const PROD_API = process.env.REACT_APP_PROD_API_URL;
const IS_STAGING = (API || '').includes('staging');

const ROLES = ['admin', 'financial', 'program_manager', 'supporting_program_manager', 'partner'];
const ROLE_LABEL = { admin: 'Admin', financial: 'Financial', program_manager: 'PM', supporting_program_manager: 'Supporting PM', partner: 'Partner' };

const card = { background: 'white', border: '1px solid #e3e8ee', borderRadius: '8px', padding: '14px 16px', marginBottom: '12px' };
const btn = (bg) => ({ padding: '5px 12px', background: bg, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
const iconBtn = { padding: '2px 8px', background: '#ecf0f1', color: '#2c3e50', border: '1px solid #d0d7de', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', lineHeight: 1.4 };
const input = { padding: '6px 8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' };

export default function NavAdmin() {
  const [data, setData] = useState({ groups: [], top_level: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newGroup, setNewGroup] = useState('');
  const [addKey, setAddKey] = useState('');
  const [addTarget, setAddTarget] = useState('top');
  const refreshNav = useNavRefresh();
  const { toast, showToast, dismissToast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    return axios.get(`${API}/nav/admin`)
      .then(res => { setData(res.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load nav config'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Run a mutation, then reload this page's list AND the real nav bar above it,
  // so the change is visible without leaving the page. Every change saves on its
  // own (there is no Save button); the toast is the confirmation that it did.
  // Guards against overlapping clicks.
  const run = useCallback(async (promiseFactory) => {
    setBusy(true);
    try {
      await promiseFactory();
      await Promise.all([load(), refreshNav()]);
      setErr(null);
      showToast('✓ Saved — the nav bar above is updated.');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Action failed — nothing was saved.');
    } finally {
      setBusy(false);
    }
  }, [load, refreshNav, showToast]);

  // Top-level order = standalone items + groups, interleaved by sort_order.
  const topNodes = useMemo(() => ([
    ...data.top_level.map(it => ({ kind: 'item', id: it.id, sort_order: it.sort_order, ref: it })),
    ...data.groups.map(g => ({ kind: 'group', id: g.id, sort_order: g.sort_order, ref: g })),
  ].sort((a, b) => a.sort_order - b.sort_order)), [data]);

  const patchPath = (node) => node.kind === 'group' ? `${API}/nav/groups/${node.id}` : `${API}/nav/items/${node.id}`;

  // Renumber a container to 10,20,30… and PATCH only what changed.
  const persistOrder = (nodes) => run(() => Promise.all(
    nodes.map((n, i) => {
      const want = (i + 1) * 10;
      if (n.sort_order === want) return null;
      return axios.patch(patchPath(n), { sort_order: want });
    }).filter(Boolean)
  ));

  const reorder = (nodes, index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= nodes.length) return;
    const arr = [...nodes];
    [arr[index], arr[j]] = [arr[j], arr[index]];
    persistOrder(arr);
  };

  const pullFromProd = () => {
    if (!window.confirm('Pull live navigation from production? This overwrites the staging nav config below — any unsaved staging-only nav changes will be lost.')) return;
    run(() => axios.get(`${PROD_API}/nav/admin`).then(res => axios.post(`${API}/nav/import`, res.data)));
  };

  if (loading && !data.groups.length && !data.top_level.length) {
    return <div style={{ padding: '24px' }}>Loading navigation…</div>;
  }

  const groupOptions = data.groups.map(g => ({ value: String(g.id), label: g.label }));
  const pageOptions = REGISTERED_PAGES.map(p => ({ value: p.key, label: `${p.label} — ${p.key}` }));

  return (
    <div style={{ maxWidth: '960px' }}>
      <h2 style={{ color: '#2c3e50', marginBottom: '4px' }}>Navigation</h2>
      <p style={{ color: '#7f8c8d', fontSize: '13px', marginTop: 0, lineHeight: 1.5 }}>
        Arrange the top nav: reorder, group items under dropdowns, relabel, and set
        which roles <strong>see</strong> each link. This controls visibility only —
        actual page access is still governed by <code>canAccess()</code> and the
        backend. A <span style={{ color: '#c0392b' }}>⚠</span> marks a link shown to a
        role that access control blocks (the link would redirect home).
        {' '}Every change saves as soon as you make it, and the nav bar updates right away.
      </p>

      {err && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>{err}</div>}

      {IS_STAGING && (
        <div style={{ ...card, background: '#fff8e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '13px', color: '#7a5b00' }}>Staging nav can drift from production without a full DB sync.</span>
          <button style={btn('#e67e22')} disabled={busy} onClick={pullFromProd}>⬇ Pull from Production</button>
        </div>
      )}

      {/* Add controls */}
      <div style={{ ...card, background: '#f8fafc', display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>New dropdown</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input style={input} placeholder="Dropdown label" value={newGroup} onChange={e => setNewGroup(e.target.value)} />
            <button
              style={btn('#27ae60')}
              disabled={busy || !newGroup.trim()}
              onClick={() => run(() => axios.post(`${API}/nav/groups`, { label: newGroup.trim(), sort_order: (topNodes.length + 1) * 10 }).then(() => setNewGroup('')))}
            >+ Add dropdown</button>
          </div>
        </div>

        <div style={{ minWidth: '300px' }}>
          <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>Add a page to the nav</div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ minWidth: '220px' }}>
              <SearchableSelect options={pageOptions} value={addKey} onChange={setAddKey} placeholder="Pick a page…" />
            </div>
            <select style={input} value={addTarget} onChange={e => setAddTarget(e.target.value)}>
              <option value="top">Top level</option>
              {data.groups.map(g => <option key={g.id} value={String(g.id)}>{g.label}</option>)}
            </select>
            <button
              style={btn('#27ae60')}
              disabled={busy || !addKey}
              onClick={() => {
                const reg = REGISTERED_PAGES.find(p => p.key === addKey);
                return run(() => axios.post(`${API}/nav/items`, {
                  group_id: addTarget === 'top' ? null : Number(addTarget),
                  page_key: addKey,
                  label: reg ? reg.label : addKey,
                  roles: ['admin'],
                  sort_order: 9990,
                }).then(() => setAddKey('')));
              }}
            >+ Add</button>
          </div>
        </div>
      </div>

      {/* Top-level interleaved order */}
      {topNodes.map((node, i) => (
        <div key={`${node.kind}-${node.id}`} style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ReorderArrows index={i} count={topNodes.length} disabled={busy} onMove={dir => reorder(topNodes, i, dir)} />
            {node.kind === 'group'
              ? <GroupHeader group={node.ref} busy={busy} run={run} />
              : <ItemRow item={node.ref} groupOptions={groupOptions} busy={busy} run={run} />}
          </div>

          {node.kind === 'group' && (
            <div style={{ marginTop: '10px', paddingLeft: '18px', borderLeft: '2px solid #eef2f6' }}>
              {node.ref.items.length === 0 && <div style={{ color: '#999', fontSize: '12px', padding: '6px 0' }}>No items — add a page to this dropdown above.</div>}
              {node.ref.items.map((it, j) => (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: j < node.ref.items.length - 1 ? '1px solid #f3f6f9' : 'none' }}>
                  <ReorderArrows index={j} count={node.ref.items.length} disabled={busy} onMove={dir => reorder(node.ref.items.map(x => ({ kind: 'item', id: x.id, sort_order: x.sort_order })), j, dir)} />
                  <ItemRow item={it} groupOptions={groupOptions} busy={busy} run={run} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <Toast toast={toast} onDismiss={dismissToast} duration={3000} />
    </div>
  );
}

function ReorderArrows({ index, count, disabled, onMove }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <button style={{ ...iconBtn, padding: '0 6px', opacity: index === 0 ? 0.3 : 1 }} disabled={disabled || index === 0} onClick={() => onMove(-1)} title="Move up">▲</button>
      <button style={{ ...iconBtn, padding: '0 6px', opacity: index === count - 1 ? 0.3 : 1 }} disabled={disabled || index === count - 1} onClick={() => onMove(1)} title="Move down">▼</button>
    </div>
  );
}

function GroupHeader({ group, busy, run }) {
  const [label, setLabel] = useState(group.label);
  useEffect(() => { setLabel(group.label); }, [group.label]);
  const save = () => { if (label.trim() && label !== group.label) run(() => axios.patch(`${API}/nav/groups/${group.id}`, { label: label.trim() })); };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
      <span style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Dropdown</span>
      <input style={{ ...input, fontWeight: 600, color: '#2c3e50' }} value={label} onChange={e => setLabel(e.target.value)} onBlur={save} onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()} />
      <button
        style={{ ...btn('#e74c3c'), marginLeft: 'auto' }}
        disabled={busy}
        onClick={() => { if (window.confirm(`Delete the "${group.label}" dropdown? Its items move back to the top level.`)) run(() => axios.delete(`${API}/nav/groups/${group.id}`)); }}
      >Delete dropdown</button>
    </div>
  );
}

function ItemRow({ item, groupOptions, busy, run }) {
  const [label, setLabel] = useState(item.label);
  useEffect(() => { setLabel(item.label); }, [item.label]);
  const saveLabel = () => { if (label.trim() && label !== item.label) run(() => axios.patch(`${API}/nav/items/${item.id}`, { label: label.trim() })); };

  const toggleRole = (role) => {
    const roles = item.roles.includes(role) ? item.roles.filter(r => r !== role) : [...item.roles, role];
    run(() => axios.patch(`${API}/nav/items/${item.id}`, { roles }));
  };
  const moveTo = (value) => run(() => axios.patch(`${API}/nav/items/${item.id}`, { group_id: value === 'top' ? null : Number(value) }));

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, flexWrap: 'wrap' }}>
      <input style={{ ...input, minWidth: '170px' }} value={label} onChange={e => setLabel(e.target.value)} onBlur={saveLabel} onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()} />
      <code style={{ fontSize: '11px', color: '#7f8c8d', background: '#f4f6f8', padding: '1px 5px', borderRadius: '3px' }}>{item.page_key}</code>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {ROLES.map(role => {
          const checked = item.roles.includes(role);
          const blocked = checked && !canAccess(role, item.page_key);
          return (
            <label key={role} title={blocked ? `Shown to ${ROLE_LABEL[role]} but blocked by access control` : ''} style={{ fontSize: '12px', color: blocked ? '#c0392b' : '#444', display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }}>
              <input type="checkbox" checked={checked} disabled={busy} onChange={() => toggleRole(role)} />
              {ROLE_LABEL[role]}{blocked && ' ⚠'}
            </label>
          );
        })}
      </div>

      <label style={{ fontSize: '12px', color: '#444', display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }} title="Hide this item while an admin is impersonating">
        <input type="checkbox" checked={item.hide_when_impersonating} disabled={busy} onChange={() => run(() => axios.patch(`${API}/nav/items/${item.id}`, { hide_when_impersonating: !item.hide_when_impersonating }))} />
        hide on impersonate
      </label>

      <label style={{ fontSize: '12px', color: '#444', display: 'flex', alignItems: 'center', gap: '3px', cursor: 'pointer' }} title="Disable without removing">
        <input type="checkbox" checked={item.enabled} disabled={busy} onChange={() => run(() => axios.patch(`${API}/nav/items/${item.id}`, { enabled: !item.enabled }))} />
        enabled
      </label>

      <select style={{ ...input, fontSize: '12px' }} value={item.group_id == null ? 'top' : String(item.group_id)} disabled={busy} onChange={e => moveTo(e.target.value)}>
        <option value="top">Top level</option>
        {groupOptions.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
      </select>

      <button style={{ ...iconBtn, color: '#c0392b', marginLeft: 'auto' }} disabled={busy} onClick={() => { if (window.confirm(`Remove "${item.label}" from the nav?`)) run(() => axios.delete(`${API}/nav/items/${item.id}`)); }}>Remove</button>
    </div>
  );
}
